import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import api from '@/lib/axios';
import { supabase, setAccessToken } from '@/lib/supabase';
import { isAxiosError } from 'axios';
import i18n from '@/i18n/config';
import { sanitizeLanguage, setStoredLanguage } from '@/lib/i18n-safe';

interface RankData {
    level: number;
    min_xp: number;
    title: string;
    narrative: string;
    color: string;
    icon: string;
}

export interface User {
    id: string;
    username: string;
    email: string;
    display_name?: string;
    bio?: string;
    avatar_url?: string;
    tier?: 'free' | 'plus' | 'pro';
    profile?: {
        experience_level?: string;
        financial_goals?: string;
        notification_prefs?: Record<string, unknown>;
        preferred_language?: string;
    };
    date_joined?: string;
    xp_points?: number;
    current_streak?: number;
    lessons_completed?: number;
    gamified_status?: string;
    streak_at_risk?: boolean;
    rank?: RankData;
    next_rank?: RankData | null;
}

export interface AuthCredentials {
    email?: string;
    password?: string;
    // UI language at sign-up. Stored in Supabase user_metadata and read once by the backend
    // when it creates the Django user (apps/api/authentication.py).
    language?: string;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (credentials: AuthCredentials) => Promise<void>;
    loginWithMagicLink: (credentials: Pick<AuthCredentials, 'email' | 'language'>) => Promise<void>;
    signup: (credentials: AuthCredentials) => Promise<void>;
    logout: () => Promise<void>;
    checkAuth: () => Promise<void>;
    updateUserData: (patch: Partial<User>) => void;
}

// A Render free-tier instance can take 30-60 s to wake up; the default axios timeout is 15 s.
const AUTH_BACKEND_TIMEOUT_MS = 60000;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Throws on failure. Used by login()/signup() so a backend problem is shown to the user
    // instead of looking like a login that silently did nothing.
    const loadUser = async (timeout?: number) => {
        const userResponse = await api.get('/api/v1/users/me/', timeout ? { timeout } : undefined);
        setUser(userResponse.data);

        const serverLang = userResponse.data?.profile?.preferred_language;
        if (serverLang) {
            const safe = sanitizeLanguage(serverLang);
            if (safe !== sanitizeLanguage(i18n.language)) {
                i18n.changeLanguage(safe);
                setStoredLanguage(safe);
            }
        }
    };

    // Tolerant variant for background paths (page load, auth state events): a degraded
    // backend must not break the whole app for visitors.
    const fetchDjangoUser = async () => {
        try {
            await loadUser();
        } catch (error) {
            if (isAxiosError(error) && error.response?.status !== 401 && error.response?.status !== 403) {
                console.error("Backend degraded during fetchDjangoUser:", error);
            }
            setUser(null);
        }
    };

    const clearUser = () => {
        setUser(null);
    };

    const checkAuth = async () => {
        try {
            await fetchDjangoUser();
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        let mounted = true;

        const init = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!mounted) return;

            setAccessToken(session?.access_token ?? null);
            if (session) {
                await fetchDjangoUser();
            }
            setIsLoading(false);
        };

        init();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setAccessToken(session?.access_token ?? null);
            if (session) {
                fetchDjangoUser();
            } else {
                clearUser();
            }
        });

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'logout_event') {
                setAccessToken(null);
                setUser(null);
            }
        };
        window.addEventListener('storage', handleStorageChange);

        return () => {
            mounted = false;
            subscription.unsubscribe();
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const login = async (credentials: AuthCredentials) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email: credentials.email!,
            password: credentials.password!,
        });
        if (error) {
            throw { response: { data: { non_field_errors: [error.message] } } };
        }
        if (data.session) {
            setAccessToken(data.session.access_token);
            await loadUser(AUTH_BACKEND_TIMEOUT_MS);
        }
    };

    const loginWithMagicLink = async (credentials: Pick<AuthCredentials, 'email' | 'language'>) => {
        const { error } = await supabase.auth.signInWithOtp({
            email: credentials.email!,
            options: { data: { preferred_language: credentials.language } },
        });
        if (error) {
            throw { response: { data: { non_field_errors: [error.message] } } };
        }
    };

    const signup = async (credentials: AuthCredentials) => {
        const { data, error } = await supabase.auth.signUp({
            email: credentials.email!,
            password: credentials.password!,
            options: { data: { preferred_language: credentials.language } },
        });
        if (error) {
            throw { response: { data: { non_field_errors: [error.message] } } };
        }
        // When email confirmation is disabled, signUp returns a session immediately.
        // Eagerly set the token and fetch the Django user so the caller can navigate
        // without hitting a race against onAuthStateChange.
        if (data.session) {
            setAccessToken(data.session.access_token);
            await loadUser(AUTH_BACKEND_TIMEOUT_MS);
        }
    };

    const logout = async () => {
        try {
            await supabase.auth.signOut();
        } catch {
            console.warn("Supabase sign-out failed, clearing frontend state anyway.");
        } finally {
            setAccessToken(null);
            setUser(null);
            localStorage.setItem('logout_event', Date.now().toString());
        }
    };

    const updateUserData = (patch: Partial<User>) => {
        setUser(prev => prev ? { ...prev, ...patch } : prev);
    };

    const value = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        loginWithMagicLink,
        signup,
        logout,
        checkAuth,
        updateUserData,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
